
import React, { useState } from 'react';
import { ServiceEntry, UserProfile } from '../types';
import { HandHeart, Coffee, Phone, Users, Heart, Plus, Calendar } from 'lucide-react';

interface ServiceLedgerProps {
  profile: UserProfile;
  onUpdate: (profile: UserProfile) => void;
}

export const ServiceLedger: React.FC<ServiceLedgerProps> = ({ profile, onUpdate }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [action, setAction] = useState('');
  const [category, setCategory] = useState<ServiceEntry['category']>('meeting');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!action.trim()) return;

    const newEntry: ServiceEntry = {
      id: Date.now().toString(),
      date,
      action,
      category,
    };

    // Sort by date descending
    const updatedHistory = [newEntry, ...(profile.serviceHistory || [])].sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    onUpdate({ ...profile, serviceHistory: updatedHistory });
    setIsAdding(false);
    setAction('');
    setCategory('meeting');
  };

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
        case 'meeting': return <Coffee className="w-4 h-4 text-amber-600" />;
        case 'sponsorship': return <Users className="w-4 h-4 text-blue-600" />;
        case 'call': return <Phone className="w-4 h-4 text-green-600" />;
        case 'community': return <HandHeart className="w-4 h-4 text-purple-600" />;
        default: return <Heart className="w-4 h-4 text-red-600" />;
    }
  };

  const getCategoryLabel = (cat: string) => {
    switch (cat) {
        case 'meeting': return 'Meeting Service';
        case 'sponsorship': return 'Sponsorship';
        case 'call': return '12-Step Call';
        case 'community': return 'Community';
        default: return 'Other';
    }
  };

  return (
    <div className="space-y-6">
       {/* Header Card */}
       <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white opacity-10 rounded-full blur-2xl"></div>
            <div className="relative z-10">
                <h2 className="text-3xl font-bold flex items-center gap-3">
                    <HandHeart className="w-8 h-8" /> 12th Step Ledger
                </h2>
                <p className="mt-2 text-emerald-100 max-w-xl italic">
                    "Practical experience shows that nothing will so much insure immunity from drinking as intensive work with other alcoholics." 
                    <span className="block text-xs mt-1 not-italic opacity-80">— Alcoholics Anonymous, p. 89</span>
                </p>
                <div className="mt-6 flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                        <span className="text-2xl font-bold block">{profile.serviceHistory?.length || 0}</span>
                        <span className="text-xs text-emerald-100 uppercase tracking-wider">Acts of Service</span>
                    </div>
                    <button 
                        onClick={() => setIsAdding(true)}
                        className="bg-white text-emerald-700 hover:bg-emerald-50 px-5 py-3 rounded-xl font-bold flex items-center gap-2 transition-colors shadow-lg"
                    >
                        <Plus className="w-5 h-5" /> Log Service
                    </button>
                </div>
            </div>
       </div>

       {/* Add Entry Form */}
       {isAdding && (
           <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-slate-200 dark:border-slate-700 animate-in slide-in-from-top-4">
               <h3 className="font-bold dark:text-white mb-4">Log New Service Action</h3>
               <form onSubmit={handleAdd} className="space-y-4">
                   <div>
                       <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">What did you do?</label>
                       <input 
                           value={action}
                           onChange={(e) => setAction(e.target.value)}
                           placeholder="e.g. Made coffee at the 7am meeting, Called a newcomer..."
                           className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                           autoFocus
                       />
                   </div>
                   <div className="grid grid-cols-2 gap-4">
                       <div>
                           <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
                           <select 
                               value={category}
                               onChange={(e) => setCategory(e.target.value as ServiceEntry['category'])}
                               className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                           >
                               <option value="meeting">Meeting Service</option>
                               <option value="sponsorship">Sponsorship</option>
                               <option value="call">12-Step Call</option>
                               <option value="community">Community/General</option>
                               <option value="other">Other</option>
                           </select>
                       </div>
                       <div>
                           <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Date</label>
                           <input 
                               type="date"
                               value={date}
                               onChange={(e) => setDate(e.target.value)}
                               className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                           />
                       </div>
                   </div>
                   <div className="flex justify-end gap-3 pt-2">
                       <button 
                           type="button" 
                           onClick={() => setIsAdding(false)}
                           className="px-4 py-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
                       >
                           Cancel
                       </button>
                       <button 
                           type="submit"
                           className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-bold shadow-md"
                       >
                           Save Entry
                       </button>
                   </div>
               </form>
           </div>
       )}

       {/* Ledger List */}
       <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
           <div className="p-6 border-b border-slate-200 dark:border-slate-700">
               <h3 className="font-bold text-lg dark:text-white">Service History</h3>
           </div>
           
           {!profile.serviceHistory || profile.serviceHistory.length === 0 ? (
               <div className="p-12 text-center text-slate-400">
                   <HandHeart className="w-12 h-12 mx-auto mb-3 opacity-20" />
                   <p>No service recorded yet. Start today!</p>
               </div>
           ) : (
               <div className="divide-y divide-slate-100 dark:divide-slate-700">
                   {profile.serviceHistory.map((entry) => (
                       <div key={entry.id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex items-center gap-4">
                           <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                               entry.category === 'meeting' ? 'bg-amber-100 dark:bg-amber-900/30' :
                               entry.category === 'sponsorship' ? 'bg-blue-100 dark:bg-blue-900/30' :
                               entry.category === 'call' ? 'bg-green-100 dark:bg-green-900/30' :
                               'bg-purple-100 dark:bg-purple-900/30'
                           }`}>
                               {getCategoryIcon(entry.category)}
                           </div>
                           <div className="flex-1">
                               <div className="flex justify-between items-start">
                                   <h4 className="font-medium text-slate-900 dark:text-white">{entry.action}</h4>
                                   <span className="text-xs text-slate-500 flex items-center gap-1">
                                       <Calendar className="w-3 h-3" />
                                       {new Date(entry.date).toLocaleDateString()}
                                   </span>
                               </div>
                               <p className="text-xs text-slate-500 mt-0.5 uppercase tracking-wide font-bold">
                                   {getCategoryLabel(entry.category)}
                               </p>
                           </div>
                       </div>
                   ))}
               </div>
           )}
       </div>
    </div>
  );
};
